import ContactMailIcon from '@mui/icons-material/ContactMail';
import MailIcon from '@mui/icons-material/Mail';
import { TimePicker } from '@mui/lab';
import {
    Box,
    Button,
    Divider,
    Fab,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Switch,
    Tooltip,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { DayOfWeek, PricelistGroupingType } from '../../../common/PricePrgClient';
import { WeekdayChooser } from '../../../common/WeekdayChooser';
import { RootState } from '../../../store';
import CustomerTree from '../../customer/CustomerTree';
import SendPricelistsToCustomersDialog from '../dialogs/SendPricelistsToCustomersDialog';
import SendPricelistsToMailDialog from '../dialogs/SendPricelistsToMailDialog';
import { getPricelistMailSettings, getSettingsUi } from '../redux/settingsSelectors';
import {
    showSendPricelistMailsToUserDialog,
    showSendPricelistsToCustomersDialog,
    updatePricelistMailingSchedule
} from '../redux/settingsSlices';
const mapStateToProps = (state: RootState) => {
    const settingsUi = getSettingsUi(state);
    return {
        mailSettings: getPricelistMailSettings(state),
        isSendPricelistMailsToUserDialogOpen: settingsUi.isSendPricelistsToMailDialogOpen,
        isSendPricelistsToCustomersDialogOpen: settingsUi.isSendPricelistsToCustomersDialogOpen
    };
};
type Props = ReturnType<typeof mapStateToProps>;

function toReadableName(pricelistGroupingType: PricelistGroupingType) {
    switch (pricelistGroupingType) {
        case PricelistGroupingType.ProductCategory1:
            return 'Product category 1';
        case PricelistGroupingType.ProductCategory2:
            return 'Product category 2';
        case PricelistGroupingType.ProductGroup:
            return 'Product group';
        case PricelistGroupingType.ProductSortOrder:
            return 'Product sort order';
        default:
            return '';
    }
}

const PricelistMailingSettingsTab = ({
    mailSettings,
    isSendPricelistMailsToUserDialogOpen,
    isSendPricelistsToCustomersDialogOpen
}: Props) => {
    const [timeOfDay, setTimeOfDay] = useState(new Date());
    const [weekdays, setWeekdays] = useState([] as DayOfWeek[]);
    const [enablePricelistMailing, setEnablePricelistMailing] = useState(false);
    const [pricelistMailGrouping, setPricelistMailGrouping] = useState(PricelistGroupingType.ProductGroup);

    const dispatch = useDispatch();

    useEffect(() => {
        setWeekdays(mailSettings.weekdays);
        setTimeOfDay(new Date(mailSettings.timeOfDay));
        setEnablePricelistMailing(mailSettings.enabled);
        setPricelistMailGrouping(mailSettings.pricelistGroupingType);
    }, [mailSettings]);

    const handleEnablePricelistMailing = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEnablePricelistMailing(event.target.checked);
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        dispatch(
            updatePricelistMailingSchedule.request({
                timeOfDay: timeOfDay.toISOString(),
                weekdays: weekdays,
                enabled: enablePricelistMailing,
                pricelistGroupingType: pricelistMailGrouping
            })
        );
    };

    const handleShowSendPricelistsToMailDialog = (open: boolean) => {
        dispatch(showSendPricelistMailsToUserDialog(open));
    };

    const handleShowSendPricelistsToCustomersDialog = (open: boolean) => {
        dispatch(showSendPricelistsToCustomersDialog(open));
    };

    const handleSetPricelistGrouping = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPricelistMailGrouping(Number(event.target.value));
    };

    const handleSetTimeOfDay = (date: Date) => {
        if (date != null) {
            setTimeOfDay(date);
        }
    };
    return (
        <>
            <form onSubmit={handleSubmit}>
                <Grid container direction="column" spacing={2} sx={{ maxWidth: 540 }}>
                    <Grid item>
                        <Grid container justifyContent="space-between" spacing={2}>
                            <Grid item>
                                <Typography variant="subtitle2">Pricelist mailing schedule</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={enablePricelistMailing}
                                            onChange={handleEnablePricelistMailing}
                                            color="primary"
                                        />
                                    }
                                    label={<Typography variant="body2">Enable</Typography>}
                                />
                            </Grid>
                            <Grid item sx={theme => ({ marginRight: theme.spacing(1) })}>
                                <Fab
                                    size="medium"
                                    color="secondary"
                                    sx={theme => ({ marginRight: theme.spacing(2) })}
                                    onClick={() => handleShowSendPricelistsToCustomersDialog(true)}
                                >
                                    <Tooltip title="Send pricelists to customers">
                                        <ContactMailIcon />
                                    </Tooltip>
                                </Fab>
                                <Fab
                                    size="medium"
                                    color="secondary"
                                    onClick={() => handleShowSendPricelistsToMailDialog(true)}
                                >
                                    <Tooltip title="Send pricelists to email">
                                        <MailIcon />
                                    </Tooltip>
                                </Fab>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Divider />
                    </Grid>
                    <Grid item>
                        <Typography variant="subtitle2">Schedule</Typography>
                        <WeekdayChooser selectedWeekdays={weekdays} setSelectedWeekdays={setWeekdays} />
                        <TimePicker
                            autoOk
                            ampm={false}
                            label="Time"
                            value={timeOfDay}
                            variant="inline"
                            onChange={handleSetTimeOfDay}
                            PopoverProps={{}}
                        />
                    </Grid>
                    <Grid item>
                        <Divider />
                    </Grid>
                    <Grid item>
                        <FormControl>
                            <FormLabel>
                                <Typography variant="subtitle2">Group articles by:</Typography>
                            </FormLabel>
                            <RadioGroup
                                sx={{ marginTop: '.4rem' }}
                                value={pricelistMailGrouping}
                                onChange={handleSetPricelistGrouping}
                            >
                                {Object.keys(PricelistGroupingType)
                                    .map(x => Number(x))
                                    .filter(x => !isNaN(x))
                                    .map(idx => [idx, toReadableName(idx as PricelistGroupingType)] as const)
                                    .map(([idx, name]) => (
                                        <FormControlLabel
                                            key={idx}
                                            sx={{ maxHeight: '2rem' }}
                                            value={idx}
                                            control={<Radio />}
                                            label={<Typography variant="body2">{name}</Typography>}
                                        />
                                    ))}
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <Divider />
                    </Grid>
                    <Grid item>
                        <Typography variant="subtitle2">
                            Following customers will receive pricelist mails
                        </Typography>
                        <Box
                            component="div"
                            sx={theme => ({
                                marginTop: theme.spacing(2),
                                padding: theme.spacing(2),
                                height: 475,
                                width: 500,
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                border: '1px solid rgba(0, 0, 0, 0.12)'
                            })}
                        >
                            <CustomerTree
                                multiSelect={false}
                                disableSelection
                                filter={customer => mailSettings.customerIds.includes(customer.customerId)}
                            />
                        </Box>
                    </Grid>
                    <Grid item>
                        <Button variant="contained" color="primary" type="submit">
                            Save
                        </Button>
                    </Grid>
                </Grid>
            </form>

            {isSendPricelistMailsToUserDialogOpen && (
                <SendPricelistsToMailDialog closeDialog={() => handleShowSendPricelistsToMailDialog(false)} />
            )}

            {isSendPricelistsToCustomersDialogOpen && (
                <SendPricelistsToCustomersDialog
                    closeDialog={() => handleShowSendPricelistsToCustomersDialog(false)}
                />
            )}
        </>
    );
};
export default connect(mapStateToProps)(PricelistMailingSettingsTab);
